import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { requestPasswordReset } from "@/api/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, SectionLabel } from "@/components/ui/Card";
import { AuthLanguagePicker } from "@/components/AuthLanguagePicker";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await requestPasswordReset(email);
    } catch {
      // Always show the same screen — enumeration-safe.
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  }

  if (submitted) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <AuthLanguagePicker />
        <div className="mb-8 text-center">
          <h1 className="font-serif text-[34px] font-medium tracking-tight">Mavinty</h1>
          <p className="mt-2 font-serif text-[15px] italic text-taupe">
            Your intelligent endurance coach
          </p>
        </div>
        <Card className="space-y-3">
          <SectionLabel>{t("auth.reset_check_email_title")}</SectionLabel>
          <p className="text-[14px] text-navy-deep">
            {t("auth.reset_sent_before")} <span className="font-medium">{email}</span>{" "}
            {t("auth.reset_sent_after")}
          </p>
          <p className="text-[13px] text-taupe">
            {t("auth.reset_not_arrived")}{" "}
            <span className="font-medium text-navy-deep">
              Mavinty Coach &lt;noreply@mavinty.com&gt;
            </span>
            .
          </p>
          <div className="border-t border-cream-border pt-4 text-center text-[13px]">
            <Link to="/login" className="font-medium text-amber-dark hover:underline">
              {t("auth.back_to_login")}
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <div className="mb-8 text-center">
        <h1 className="font-serif text-[34px] font-medium tracking-tight">Mavinty</h1>
        <p className="mt-2 font-serif text-[15px] italic text-taupe">
          Your intelligent endurance coach
        </p>
      </div>
      <Card className="space-y-4">
        <div>
          <h2 className="font-serif text-[20px] font-medium text-navy-deep">
            {t("auth.forgot_title")}
          </h2>
          <p className="mt-1 text-[13px] text-taupe">
            {t("auth.forgot_lede")}
          </p>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Input
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <Button type="submit" size="lg" className="w-full" loading={loading}>
            {t("auth.forgot_submit")}
          </Button>
        </form>
        <div className="border-t border-cream-border pt-4 text-center text-[13px]">
          <Link to="/login" className="font-medium text-amber-dark hover:underline">
            {t("auth.back_to_login_short")}
          </Link>
        </div>
      </Card>
    </div>
  );
}
